/**
 * SoleadLab 2.0 - PDF Page Selector Modal
 *
 * When uploading a multi-page PDF as an overlay, this modal shows
 * thumbnail previews of all pages and lets the user pick which one to use.
 * Single-page PDFs skip this modal entirely.
 */

import { useState, useEffect, useRef } from 'react';
import './PdfPageSelector.css';

interface PdfPageSelectorProps {
  file: File;
  onSelect: (pageNumber: number) => void;
  onCancel: () => void;
}

interface PageThumb {
  pageNum: number;
  dataUrl: string;
}

export default function PdfPageSelector({ file, onSelect, onCancel }: PdfPageSelectorProps) {
  const [thumbs, setThumbs] = useState<PageThumb[]>([]);
  const [totalPages, setTotalPages] = useState(0);
  const [selectedPage, setSelectedPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const cancelledRef = useRef(false);

  // Generate thumbnails for all pages
  useEffect(() => {
    cancelledRef.current = false;
    const revokables: string[] = [];

    (async () => {
      try {
        const pdfjsLib = await import('pdfjs-dist/legacy/build/pdf.mjs');
        pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
          'pdfjs-dist/legacy/build/pdf.worker.min.mjs',
          import.meta.url
        ).toString();

        const arrayBuffer = await file.arrayBuffer();
        const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
        setTotalPages(pdf.numPages);

        const generated: PageThumb[] = [];

        for (let i = 1; i <= pdf.numPages; i++) {
          if (cancelledRef.current) break;

          const page = await pdf.getPage(i);
          // Small scale for thumbnails
          const thumbScale = 1;
          const viewport = page.getViewport({ scale: thumbScale });

          const canvas = document.createElement('canvas');
          canvas.width = viewport.width;
          canvas.height = viewport.height;
          const ctx = canvas.getContext('2d')!;

          ctx.fillStyle = 'white';
          ctx.fillRect(0, 0, canvas.width, canvas.height);

          await page.render({ canvasContext: ctx, viewport, canvas } as any).promise;

          const dataUrl = canvas.toDataURL('image/jpeg', 0.7);
          generated.push({ pageNum: i, dataUrl });

          if (!cancelledRef.current) {
            setThumbs([...generated]);
          }
        }

        setLoading(false);
      } catch (err) {
        console.error('PDF thumbnail generation failed:', err);
        setLoading(false);
      }
    })();

    return () => {
      cancelledRef.current = true;
      revokables.forEach((u) => URL.revokeObjectURL(u));
    };
  }, [file]);

  return (
    <div className="pdf-page-backdrop" onClick={(e) => e.target === e.currentTarget && onCancel()}>
      <div className="pdf-page-modal">
        {/* Header */}
        <div className="pdf-page-modal__header">
          <h3>Select PDF Page</h3>
          <button className="pdf-page-modal__close" onClick={onCancel} type="button">
            &times;
          </button>
        </div>

        <div className="pdf-page-modal__hint">
          {loading
            ? `Loading pages... (${thumbs.length}/${totalPages || '?'})`
            : `${totalPages} pages found. Select a page to use as overlay.`}
        </div>

        {/* Thumbnail grid */}
        <div className="pdf-page-grid">
          {thumbs.map((t) => (
            <button
              key={t.pageNum}
              className={`pdf-page-thumb${selectedPage === t.pageNum ? ' pdf-page-thumb--selected' : ''}`}
              onClick={() => setSelectedPage(t.pageNum)}
              type="button"
            >
              <img src={t.dataUrl} alt={`Page ${t.pageNum}`} />
              <span className="pdf-page-thumb__label">Page {t.pageNum}</span>
            </button>
          ))}
          {loading && thumbs.length === 0 && (
            <div className="pdf-page-loading">Loading PDF...</div>
          )}
        </div>

        {/* Actions */}
        <div className="pdf-page-modal__actions">
          <button
            className="pdf-page-btn pdf-page-btn--cancel"
            onClick={onCancel}
            type="button"
          >
            Cancel
          </button>
          <button
            className="pdf-page-btn pdf-page-btn--apply"
            onClick={() => onSelect(selectedPage)}
            disabled={thumbs.length === 0}
            type="button"
          >
            Use Page {selectedPage}
          </button>
        </div>
      </div>
    </div>
  );
}
